import { useCallback, useRef, useEffect } from 'react';
import { useNodeStore } from '../stores/nodeStore';
import { useProjectStore } from '../stores/projectStore';
import { useTaskStore } from '../stores/taskStore';
import { AppNode, NodeType, NodeStatus, LightingParams } from '../types';
import { generateApi, GenerateRequest } from '../services/generateApi';
import { isBackendAssetUrl, saveSingleAsset } from '../utils/assetUtils';
import { toast } from 'sonner';

export function useLightingGenerator(
    saveHistory: () => void,
    handleAssetGenerated: (type: 'image' | 'video' | 'audio', src: string, title: string) => void,
) {
    const nodes = useNodeStore((s) => s.nodes);
    const addNode = useNodeStore((s) => s.addNode);
    const addConnection = useNodeStore((s) => s.addConnection);
    const updateNode = useNodeStore((s) => s.updateNode);
    const currentProjectId = useProjectStore((s) => s.currentProjectId);
    const addTaskWithRequest = useTaskStore((s) => s.addTaskWithRequest);
    const removeTask = useTaskStore((s) => s.removeTask);
    const nodesRef = useRef(nodes);
    useEffect(() => { nodesRef.current = nodes; }, [nodes]);

    const handleGenerateLighting = useCallback(async (sourceNodeId: string, params: {
        imageUrl: string;
        model?: string;
        lightingParams: LightingParams;
        lightingRequest: { lighting_style: string };
    }) => {
        const sourceNode = nodesRef.current.find(n => n.id === sourceNodeId);
        if (!sourceNode) return;

        const newNode: AppNode = {
            id: `n-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
            type: NodeType.IMAGE_GENERATOR,
            x: sourceNode.x + 500,
            y: sourceNode.y,
            ...(sourceNode.width !== undefined && sourceNode.height !== undefined
                ? { width: sourceNode.width, height: sourceNode.height }
                : { width: 420 }),
            title: '打光',
            status: NodeStatus.WORKING,
            data: {
                prompt: '',
                model: params.model || 'fal-image-apps-v2-relighting',
            },
            inputs: [sourceNodeId],
        };

        try {
            saveHistory();
            addNode(newNode);
            addConnection(sourceNodeId, newNode.id);

            // 打灯不使用提示词：fal relighting 无 prompt 入参，主体一致性由专用模型保证（方案 §7）
            const request: GenerateRequest = {
                modelId: params.model || 'fal-image-apps-v2-relighting',
                prompt: '',
                mode: 'img2img',
                inputImages: [params.imageUrl],
                params: {
                    lightingParams: params.lightingParams,
                    lightingRequest: params.lightingRequest,
                },
                feature: 'lighting',
            };

            const asyncResult = await generateApi.generateAsync(request);
            const taskId = asyncResult.taskId;

            addTaskWithRequest(taskId, newNode.id, NodeType.IMAGE_GENERATOR, request as Record<string, any>);
            updateNode(newNode.id, { taskId });

            const statusResult = await generateApi.pollTaskUntilComplete(taskId);

            if (statusResult.status === 'failed') {
                throw new Error(statusResult.error || '打光图片生成失败');
            }

            const resultData = statusResult.result;
            if (!resultData?.results || resultData.results.length === 0) {
                throw new Error('未收到生成的打光图片');
            }

            let finalUrl: string = resultData.results[0]?.url || '';
            if (!finalUrl) throw new Error('未收到生成的打光图片');
            if (currentProjectId && !isBackendAssetUrl(finalUrl)) {
                finalUrl = await saveSingleAsset(currentProjectId, finalUrl, 'lighting.png', {
                    generator_node_id: newNode.id,
                    generator_type: 'lighting',
                    model: params.model || '',
                    lighting_style: params.lightingRequest.lighting_style,
                    lightingParams: JSON.stringify(params.lightingParams),
                });
            }

            updateNode(newNode.id, {
                image: finalUrl,
            }, undefined, undefined, NodeStatus.SUCCESS);

            removeTask(taskId);
            handleAssetGenerated('image', finalUrl, `打光 · ${params.lightingRequest.lighting_style}`);
            toast.success('打光图片生成成功');
        } catch (e: any) {
            console.error('打光图片生成失败:', e);
            const pendingTask = useTaskStore.getState().getTaskByNodeId(newNode.id);
            if (pendingTask) removeTask(pendingTask.taskId);
            updateNode(newNode.id, { error: e.message }, undefined, undefined, NodeStatus.ERROR);
            toast.error('打光图片生成失败', { description: e.message, duration: 6000 });
        }
    }, [addNode, addConnection, updateNode, handleAssetGenerated, currentProjectId, saveHistory, addTaskWithRequest, removeTask]);

    return { handleGenerateLighting };
}
